import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, ExternalLink, Flame, Clock, CheckCircle2 } from "lucide-react";
import { useLang, scrollToId } from "@/i18n";
import { sortedEvents, eventStatus } from "@/eventsData";
import { fmtDate } from "@/api";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.8, delay },
});

const Badge = ({ status }) => {
  const { t } = useLang();
  if (status.key === "live")
    return <span className="inline-flex items-center gap-1.5 rounded-full bg-terracotta text-cream-light px-3 py-1 text-[11px] font-bold tracking-widest uppercase" data-testid="event-badge-live"><Flame size={12} /> {t("ev.live")}</span>;
  if (status.key === "upcoming")
    return <span className="inline-flex items-center gap-1.5 rounded-full bg-sage/30 text-green-dark px-3 py-1 text-[11px] font-bold tracking-widest uppercase" data-testid="event-badge-upcoming"><Clock size={12} /> {t("ev.upcoming").replace("{d}", status.days)}</span>;
  if (status.key === "tba")
    return <span className="inline-flex items-center gap-1.5 rounded-full bg-warm-grey/30 text-walnut px-3 py-1 text-[11px] font-bold tracking-widest uppercase" data-testid="event-badge-tba"><CalendarDays size={12} /> {t("ev.tba")}</span>;
  return <span className="inline-flex items-center gap-1.5 rounded-full bg-warm-grey/30 text-walnut px-3 py-1 text-[11px] font-bold tracking-widest uppercase" data-testid="event-badge-ended"><CheckCircle2 size={12} /> {t("ev.ended")}</span>;
};

export default function FestivalRadar() {
  const { t, lang } = useLang();
  const events = sortedEvents();

  return (
    <section id="evenimente" className="py-24 md:py-32" data-testid="festival-radar">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.p {...fadeUp()} className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3">
          <span className="inline-block w-10 h-px bg-terracotta" />
          {t("ev.eyebrow")}
        </motion.p>
        <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <motion.h2 {...fadeUp(0.1)} className="font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-main max-w-2xl">
            {t("ev.title")}
          </motion.h2>
          <motion.p {...fadeUp(0.2)} className="text-brown/80 font-light max-w-md">{t("ev.sub")}</motion.p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {events.map((ev, i) => {
            const status = eventStatus(ev);
            return (
              <motion.article
                key={ev.id}
                {...fadeUp(0.1 + (i % 3) * 0.1)}
                className={`bg-cream border rounded-3xl p-7 flex flex-col ${status.key === "live" ? "border-terracotta/60 shadow-xl shadow-terracotta/10" : "border-warm-grey/40"}`}
                data-testid={`event-card-${ev.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <Badge status={status} />
                  {ev.featured && <span className="text-terracotta text-xs font-bold tracking-widest uppercase">★</span>}
                </div>
                {ev.image && (
                  <button onClick={() => scrollToId("#rezervare")} data-testid={`event-image-${ev.id}`} aria-label={t("ev.book")}
                    className="mt-4 block w-full overflow-hidden rounded-2xl cursor-pointer group/img aspect-[1122/1402]">
                    <img src={ev.image} alt={ev.name} loading="lazy"
                      className="w-full h-full object-cover group-hover/img:scale-[1.03] transition-transform duration-700" />
                  </button>
                )}
                <h3 className="mt-4 font-serif text-2xl text-main leading-snug">{ev.name}</h3>
                <p className="mt-2 flex items-center gap-2 text-xs font-semibold text-walnut uppercase tracking-wider">
                  <CalendarDays size={13} className="text-terracotta" />
                  {fmtDate(ev.start_date)} – {fmtDate(ev.end_date)}
                </p>
                <p className="mt-1.5 flex items-center gap-2 text-xs text-brown/70">
                  <MapPin size={13} className="text-sage" />
                  {ev[`location_${lang}`] || ev.location_en || ev.location_ro} · {ev[`distance_${lang}`] || ev.distance_en || ev.distance_ro}
                </p>
                <p className="mt-4 text-sm text-brown/85 font-light leading-relaxed flex-1">
                  {ev[`desc_${lang}`] || ev.desc_en || ev.desc_ro}
                </p>
                {ev.booking_campaign && status.key !== "ended" && (ev[`campaign_${lang}`] || ev.campaign_en || ev.campaign_ro) && (
                  <p className="mt-3 text-[13px] italic text-terracotta/90 font-medium leading-relaxed border-l-2 border-terracotta/40 pl-3" data-testid={`event-campaign-${ev.id}`}>
                    {ev[`campaign_${lang}`] || ev.campaign_en || ev.campaign_ro}
                  </p>
                )}
                {ev.last_verified && (
                  <p className="mt-3 text-[11px] text-brown/50 font-light" data-testid={`event-verified-${ev.id}`}>
                    {t("ev.verified").replace("{d}", fmtDate(ev.last_verified))}
                  </p>
                )}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  {status.key !== "ended" && (
                    <button onClick={() => scrollToId("#rezervare")} data-testid={`event-book-${ev.id}`}
                      className="rounded-full bg-terracotta text-cream-light text-sm font-semibold px-5 py-2.5 hover:bg-terracotta/90 transition-all duration-300">
                      {t("ev.book")}
                    </button>
                  )}
                  {ev.page && (
                    <Link to={ev.page} data-testid={`event-page-${ev.id}`}
                      className="rounded-full border border-terracotta/60 text-terracotta text-sm font-semibold px-5 py-2.5 hover:bg-terracotta hover:text-cream-light transition-all duration-300">
                      {t("ev.details")}
                    </Link>
                  )}
                  {ev.official_url && (
                    <a href={ev.official_url} target="_blank" rel="noreferrer" data-testid={`event-official-${ev.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-brown hover:text-terracotta transition-colors">
                      {t("ev.official")} <ExternalLink size={13} />
                    </a>
                  )}
                </div>
              </motion.article>
            );
          })}
        </div>
        <motion.p {...fadeUp(0.2)} className="mt-8 text-xs text-brown/60 font-light" data-testid="events-approx-note">
          {t("ev.approx")}
        </motion.p>
      </div>
    </section>
  );
}
